import type { Vector2 } from "./vector";

export const FIGHTER_IDS = [
  "dog",
  "cat",
  "tiger",
  "dolphin",
  "horse",
  "lion",
  "elephant",
  "otter",
  "penguin",
  "wolf",
  "squirrel",
  "trex",
] as const;

export type FighterId = (typeof FIGHTER_IDS)[number];
export type PlaceholderTemplateId = "dog" | "tiger";

export type FighterSpriteState =
  | "idle"
  | "run"
  | "attack"
  | "special"
  | "grab"
  | "shield"
  | "hurt"
  | "jump";

export type FighterState =
  | FighterSpriteState
  | "ledge-hang"
  | "ledge-climb"
  | "ledge-attack"
  | "ledge-roll";

export type LedgeId = "main-left" | "main-right";

export type BufferedAction =
  | "jump"
  | "attack"
  | "special"
  | "grab"
  | "shield";

export interface InputFrame {
  moveX: -1 | 0 | 1;
  jumpPressed: boolean;
  attackPressed: boolean;
  specialPressed: boolean;
  grabPressed: boolean;
  shieldPressed: boolean;
  shieldHeld: boolean;
  dashHeld: boolean;
  downHeld: boolean;
}

export interface FighterConfig {
  id: FighterId;
  name: string;
  title: string;
  placeholderTemplate: PlaceholderTemplateId;
  accent: string;
  moveSpeed: number;
  dashSpeed: number;
  airSpeed: number;
  jumpSpeed: number;
  weight: number;
  width: number;
  height: number;
  attackDamage: number;
  attackPower: number;
  attackReach: number;
  attackHeight: number;
  specialDamage: number;
  specialPower: number;
  specialDuration: number;
  specialTriggerTime: number;
  projectileOffset: number;
  projectileSpeed: number;
  projectileRadius: number;
  projectileLife: number;
  projectileColor: string;
  grabDamage: number;
  grabReach: number;
  throwPowerForward: number;
  throwPowerBack: number;
}

export interface Fighter {
  id: FighterId;
  config: FighterConfig;
  position: Vector2;
  previousPosition: Vector2;
  velocity: Vector2;
  facing: -1 | 1;
  grounded: boolean;
  groundPlatform: string | null;
  jumpsRemaining: number;
  damage: number;
  stocks: number;
  shield: number;
  state: FighterState;
  actionTimer: number;
  actionDuration: number;
  actionInstance: number;
  actionTriggered: boolean;
  attackConnected: boolean;
  invulnerability: number;
  respawnTimer: number;
  dropTimer: number;
  airDodgeAvailable: boolean;
  flashTimer: number;
  hitPulse: number;
  bufferedAction: BufferedAction | null;
  bufferedActionFrames: number;
  comboCount: number;
  comboRecoveryFrames: number;
  comboActionInstance: number;
  comboDamageScale: number;
  comboKnockbackScale: number;
  comboHitstunScale: number;
  ledgeId: LedgeId | null;
  ledgeHangTimer: number;
  ledgeGrabCooldown: number;
  heldFighter: FighterId | null;
  grabbedBy: FighterId | null;
}

export interface Platform {
  id: string;
  x1: number;
  x2: number;
  y: number;
  oneWay: boolean;
}

export interface StageLedge {
  id: LedgeId;
  platformId: string;
  x: number;
  y: number;
  inward: -1 | 1;
}

export interface Projectile {
  id: number;
  actionInstance: number;
  owner: FighterId;
  position: Vector2;
  velocity: Vector2;
  radius: number;
  damage: number;
  power: number;
  life: number;
  color: string;
}

export type GameStatus = "title" | "countdown" | "playing" | "paused" | "gameover";

export type GameEvent =
  | { type: "announce"; text: string }
  | { type: "jump"; fighter: FighterId; x: number; y: number }
  | { type: "attack"; fighter: FighterId; x: number; y: number }
  | { type: "special"; fighter: FighterId; x: number; y: number }
  | {
      type: "ledge-grab";
      fighter: FighterId;
      ledge: LedgeId;
      x: number;
      y: number;
    }
  | { type: "shield"; fighter: FighterId; x: number; y: number }
  | { type: "shield-break"; fighter: FighterId; x: number; y: number }
  | {
      type: "hit";
      attacker: FighterId;
      defender: FighterId;
      x: number;
      y: number;
      damage: number;
      baseDamage: number;
      strength: number;
      comboCount: number;
      damageScale: number;
      knockbackScale: number;
      hitstunScale: number;
    }
  | { type: "grab"; attacker: FighterId; defender: FighterId; x: number; y: number }
  | {
      type: "throw";
      attacker: FighterId;
      defender: FighterId;
      x: number;
      y: number;
      direction: -1 | 1;
      forward: boolean;
    }
  | { type: "ko"; fighter: FighterId; x: number; y: number }
  | { type: "respawn"; fighter: FighterId; x: number; y: number }
  | { type: "gameover"; winner: FighterId };

export interface SpriteStateMeta {
  file: string;
  width: number;
  height: number;
}

export type SpriteManifest = Record<
  FighterId,
  Record<FighterSpriteState, SpriteStateMeta>
>;
